import { Capacitor } from '@capacitor/core';
import messageService from '../../services/messageService';

/**
 * Biometric unlock for secure chats (Oct 2026), two ways in behind one face:
 *
 *   native   the code sits in the Keychain / Keystore behind the device's biometrics
 *            (@capgo/capacitor-native-biometric); unlocking reads it back and hands it
 *            to the ordinary PIN endpoint — the server never learns a thing changed.
 *   web      a WebAuthn platform credential (Touch ID, Face ID, Windows Hello, an
 *            Android fingerprint) registered against /messages/secure/passkeys and
 *            verified there on every unlock; the browser never holds the code.
 *
 * `enrolled` is a per-device, per-account marker in localStorage — the server's count
 * can't say whether THIS device is one of them.
 */

export const isNative = () => Capacitor.isNativePlatform();

const flagKey = (userId) => `pr.secure.bio.${userId}`;
const serverKey = (userId) => `heyportal.app/secure-chats/${userId}`;

export function isEnrolled(userId) {
  try { return !!userId && !!localStorage.getItem(flagKey(userId)); } catch (e) { return false; }
}
/** A passkey from another device answered here: this device is enrolled from now on. */
export function markEnrolled(userId) { if (!isEnrolled(userId)) setEnrolled(userId, '1'); }
function setEnrolled(userId, value) {
  try { if (value) localStorage.setItem(flagKey(userId), value); else localStorage.removeItem(flagKey(userId)); } catch (e) { /* private mode */ }
}
/** The offer after an unlock ("use Face ID next time?") is made once per device; NOT NOW keeps it away. */
const offerKey = (userId) => `pr.secure.bio.offer.${userId}`;
export function offerDeclined(userId) {
  try { return !!userId && localStorage.getItem(offerKey(userId)) === 'declined'; } catch (e) { return true; }
}
export function declineOffer(userId) {
  try { localStorage.setItem(offerKey(userId), 'declined'); } catch (e) { /* private mode */ }
}

/** What the web enrolment stored: the server's passkey id, so OFF can delete it. */
export function enrolledPasskeyId(userId) {
  const v = isEnrolled(userId) ? localStorage.getItem(flagKey(userId)) : null;
  return v && v !== '1' ? v : null;
}

/** Which face the device has, named the way its owner would. */
export function biometricLabel(kind) {
  switch (kind) {
    case 'faceid': return 'FACE ID';
    case 'touchid': return 'TOUCH ID';
    case 'fingerprint': return 'FINGERPRINT';
    case 'hello': return 'WINDOWS HELLO';
    case 'apple': return 'FACE ID / TOUCH ID';
    default: return 'BIOMETRICS';
  }
}

let nativePlugin = null;
async function plugin() {
  if (!nativePlugin) nativePlugin = (await import('@capgo/capacitor-native-biometric')).NativeBiometric;
  return nativePlugin;
}

/** { available, kind } for this device — never throws. */
export async function biometricSupport() {
  try {
    if (isNative()) {
      const r = await (await plugin()).isAvailable();
      const kind = { 1: 'touchid', 2: 'faceid', 3: 'fingerprint', 4: 'faceid', 5: 'generic', 6: 'generic' }[r.biometryType] || 'generic';
      return { available: !!r.isAvailable, kind };
    }
    if (typeof window === 'undefined' || !window.PublicKeyCredential || !window.isSecureContext) return { available: false, kind: null };
    const ok = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    const ua = navigator.userAgent || '';
    const kind = /Windows/.test(ua) ? 'hello' : /Android/.test(ua) ? 'fingerprint' : /Mac|iPhone|iPad/.test(ua) ? 'apple' : 'generic';
    return { available: !!ok, kind };
  } catch (e) {
    return { available: false, kind: null };
  }
}

// ── WebAuthn plumbing ──

const b64url = {
  decode(s) {
    const pad = '='.repeat((4 - (s.length % 4)) % 4);
    const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out.buffer;
  },
  encode(buf) {
    const bytes = new Uint8Array(buf);
    let bin = '';
    bytes.forEach((b) => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  },
};

/** The server's options (Fido2NetLib JSON, base64url strings) → what the browser wants. */
export function toCreateOptions(o) {
  return {
    rp: o.rp,
    user: { id: b64url.decode(o.user.id), name: o.user.name, displayName: o.user.displayName },
    challenge: b64url.decode(o.challenge),
    pubKeyCredParams: o.pubKeyCredParams,
    timeout: o.timeout,
    attestation: o.attestation || 'none',
    authenticatorSelection: o.authenticatorSelection,
    excludeCredentials: (o.excludeCredentials || []).map((c) => ({ type: c.type || 'public-key', id: b64url.decode(c.id), transports: c.transports })),
  };
}
export function toGetOptions(o) {
  return {
    challenge: b64url.decode(o.challenge),
    rpId: o.rpId,
    timeout: o.timeout,
    userVerification: o.userVerification || 'required',
    allowCredentials: (o.allowCredentials || []).map((c) => ({ type: c.type || 'public-key', id: b64url.decode(c.id), transports: c.transports })),
  };
}
/** A PublicKeyCredential → the JSON the server verifies. */
export function encodeCredential(cred) {
  const r = cred.response;
  const response = { clientDataJSON: b64url.encode(r.clientDataJSON) };
  if (r.attestationObject) {
    response.attestationObject = b64url.encode(r.attestationObject);
    // The server's model insists on the list, even an empty one.
    response.transports = typeof r.getTransports === 'function' ? r.getTransports() : [];
  }
  if (r.authenticatorData) response.authenticatorData = b64url.encode(r.authenticatorData);
  if (r.signature) response.signature = b64url.encode(r.signature);
  if (r.userHandle) response.userHandle = b64url.encode(r.userHandle);
  return { id: cred.id, rawId: b64url.encode(cred.rawId), type: cred.type, response, clientExtensionResults: cred.getClientExtensionResults?.() || {} };
}

function deviceLabel() {
  const ua = navigator.userAgent || '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'This device';
}

const fail = (e) => ({ ok: false, error: (e?.message || e?.error || 'THAT DIDN’T WORK').toUpperCase(), cancelled: e?.name === 'NotAllowedError' || e?.name === 'AbortError' });

// ── Enrol / forget ──

/**
 * Turn biometrics on for this device. Native needs the code (it is what gets kept);
 * web needs the vault open on the server (the ceremony is refused otherwise).
 */
export async function enrolBiometric({ userId, code = null }) {
  try {
    if (isNative()) {
      if (!code) return { ok: false, error: 'ENTER YOUR CODE FIRST' };
      await (await plugin()).setCredentials({ username: 'secure', password: code, server: serverKey(userId) });
      setEnrolled(userId, '1');
      return { ok: true };
    }
    const opt = await messageService.passkeyRegisterOptions();
    if (!opt.success) return { ok: false, error: (opt.error || 'THAT DIDN’T WORK').toUpperCase(), locked: opt.status === 423 };
    const cred = await navigator.credentials.create({ publicKey: toCreateOptions(opt.data) });
    const r = await messageService.passkeyRegister(encodeCredential(cred), deviceLabel());
    if (!r.success) return { ok: false, error: (r.error || 'THAT DIDN’T WORK').toUpperCase(), locked: r.status === 423 };
    // Which row is ours: the newest. OFF deletes it by id.
    const list = await messageService.passkeyList();
    const mine = list.success ? (list.data || []).slice(-1)[0] : null;
    setEnrolled(userId, mine?.id || '1');
    return { ok: true, status: r.data };
  } catch (e) {
    return fail(e);
  }
}

export async function forgetBiometric({ userId }) {
  try {
    if (isNative()) {
      try { await (await plugin()).deleteCredentials({ server: serverKey(userId) }); } catch (e) { /* nothing stored */ }
    } else {
      const id = enrolledPasskeyId(userId);
      if (id) await messageService.passkeyDelete(id);
    }
  } finally {
    setEnrolled(userId, null);
  }
  return { ok: true };
}

/** After the code changed: a native device keeps the new one behind the same face. */
export async function refreshBiometricCode({ userId, code }) {
  if (!isNative() || !isEnrolled(userId) || !code) return;
  try { await (await plugin()).setCredentials({ username: 'secure', password: code, server: serverKey(userId) }); } catch (e) { /* re-enrol later */ }
}

// ── Unlock ──

/**
 * Ask the device for its face. Native answers `{ ok, code }` for the PIN endpoint;
 * web answers `{ ok, result }` — the server's SecureUnlockResultDto, already unlocked.
 * A cancelled prompt comes back `{ ok: false, cancelled: true }` with no error to show.
 */
export async function biometricUnlock({ userId, label = 'BIOMETRICS' }) {
  try {
    if (isNative()) {
      const p = await plugin();
      let creds;
      try {
        creds = await p.getSecureCredentials({ server: serverKey(userId), reason: 'Unlock your secure chats', title: 'Secure chats' });
      } catch (e) {
        // Stored without biometric protection (older plugin, some Android keystores): prompt, then read.
        if (e?.code === 21 || /no protected/i.test(e?.message || '')) {
          await p.verifyIdentity({ reason: 'Unlock your secure chats', title: 'Secure chats', negativeButtonText: 'Use PIN' });
          creds = await p.getCredentials({ server: serverKey(userId) });
        } else throw e;
      }
      if (!creds?.password) { setEnrolled(userId, null); return { ok: false, error: `${label} ISN’T SET UP HERE` }; }
      return { ok: true, code: creds.password };
    }
    const opt = await messageService.passkeyAssertOptions();
    if (!opt.success) {
      if (/no passkey/i.test(opt.error || '')) setEnrolled(userId, null);
      return { ok: false, error: (opt.error || 'THAT DIDN’T WORK').toUpperCase() };
    }
    const cred = await navigator.credentials.get({ publicKey: toGetOptions(opt.data) });
    const r = await messageService.passkeyAssert(encodeCredential(cred));
    if (!r.success) return { ok: false, error: (r.error || 'THAT DIDN’T WORK').toUpperCase() };
    return { ok: true, result: r.data };
  } catch (e) {
    const f = fail(e);
    // A native prompt the person dismissed, or a cancelled browser sheet: not an error.
    if (f.cancelled || e?.code === 10 || e?.code === 16 || /cancel/i.test(e?.message || '')) return { ok: false, cancelled: true };
    return f;
  }
}
